import { useMemo } from 'react';
import type { PlaceObject } from '../types';
import { getPlaceCategory, CATEGORY_ORDER } from '../lib/placeCategory';
import { placeBelongsToModule } from '../lib/placeMembership';

export interface PlaceFolder {
  id: string;
  label: string;
  objectCount: number | undefined;
}

/**
 * The two place-list memos:
 *   - `places`: archived/search/folder filter + rating/alpha sort
 *   - `groupedFolders`: folder→places mapping, dominant-category grouping
 */
export function usePlaceList(params: {
  allPlaces: PlaceObject[];
  isPlacesContainer: boolean;
  currentFolder: { id: string; label: string } | null;
  moduleId: string;
  showArchivedPlaces: boolean;
  searchQuery: string;
  placeSort: 'rating' | 'alpha';
  placeFolders: PlaceFolder[];
  folderSearch: string;
}) {
  const {
    allPlaces,
    isPlacesContainer,
    currentFolder,
    moduleId,
    showArchivedPlaces,
    searchQuery,
    placeSort,
    placeFolders,
    folderSearch,
  } = params;

  const places = useMemo(() => {
    const normalizedSearch = searchQuery?.trim().toLowerCase();
    return allPlaces.filter((place: any) => {
      const isArchived = place.status === 'archived' || place.archived_at || place.archivedAt;
      if (showArchivedPlaces) {
        if (!isArchived) return false;
      } else {
        if (isArchived) return false;
      }

      if (normalizedSearch) {
        const haystack = [place.name, place.title, place.address, place.description]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(normalizedSearch)) {
          return false;
        }
      }

      // For places container: filter by current folder client-side
      // When no folder selected (top-level or "All" mode), show all
      const filterModuleId = isPlacesContainer ? currentFolder?.id : moduleId;
      if (!filterModuleId) return true;

      // Legacy single moduleId support
      if (place.moduleId) return place.moduleId === filterModuleId;

      // moduleIds array (from backend normalization)
      if (place.moduleIds?.length) {
        return place.moduleIds.includes(filterModuleId);
      }

      // moduleLinks array support - require explicit link
      return place.moduleLinks?.some((link: any) => link.moduleId === filterModuleId);
    }).sort((a: any, b: any) => {
      if (placeSort === 'alpha') {
        return (a.label || a.title || '').localeCompare(b.label || b.title || '');
      }
      // Sort by user rating descending (rated places first), then by created_at
      const aRating = a.userRating ?? 0;
      const bRating = b.userRating ?? 0;
      if (aRating !== bRating) return bRating - aRating;
      const aTime = a.created_at || a.createdAt || '';
      const bTime = b.created_at || b.createdAt || '';
      return aTime < bTime ? -1 : aTime > bTime ? 1 : 0;
    });
  }, [allPlaces, isPlacesContainer, currentFolder?.id, moduleId, showArchivedPlaces, searchQuery, placeSort]);

  // Group folders by dominant category of their places, with place name previews
  const groupedFolders = useMemo(() => {
    if (!isPlacesContainer || placeFolders.length === 0) return [];

    // Build folder → places mapping and determine dominant category
    const enriched = placeFolders.map(folder => {
      const folderPlaces = allPlaces.filter((p: any) =>
        placeBelongsToModule(p, folder.id, { includeModuleLinks: true })
      );

      const catCounts: Record<string, number> = {};
      for (const p of folderPlaces) {
        const cat = getPlaceCategory((p as any).primaryType, p as any);
        catCounts[cat] = (catCounts[cat] || 0) + 1;
      }
      // Folder label overrides the counted dominant category.
      const labelLower = folder.label.toLowerCase();
      let dominantCat = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Other';
      if (labelLower.includes('restaurant') || labelLower.includes('dining') || labelLower.includes('food')) dominantCat = 'Restaurants';
      else if (labelLower.includes('hotel') || labelLower.includes('lodging') || labelLower.includes('resort')) dominantCat = 'Hotels';
      else if (labelLower.includes('cafe') || labelLower.includes('coffee')) dominantCat = 'Cafes';
      else if (labelLower.includes('bar') || labelLower.includes('club')) dominantCat = 'Bars';
      else if (labelLower.includes('museum') || labelLower.includes('gallery')) dominantCat = 'Museums';
      else if (labelLower.includes('chateau') || labelLower.includes('château') || labelLower.includes('castle') || labelLower.includes('attraction') || labelLower.includes('sight')) dominantCat = 'Attractions';
      else if (labelLower.includes('park') || labelLower.includes('garden')) dominantCat = 'Parks';
      else if (labelLower.includes('shop') || labelLower.includes('store') || labelLower.includes('market')) dominantCat = 'Shopping';

      const preview = folderPlaces
        .slice(0, 3)
        .map((p: any) => p.title || p.name || '')
        .filter(Boolean)
        .join(' · ');
      const hasMore = folderPlaces.length > 3;

      return { ...folder, category: dominantCat, preview: preview + (hasMore ? '…' : ''), placeCount: folderPlaces.length };
    });

    const filtered = folderSearch
      ? enriched.filter(f => f.label.toLowerCase().includes(folderSearch.toLowerCase()) || f.preview.toLowerCase().includes(folderSearch.toLowerCase()))
      : enriched;

    // Group by category, maintaining a sensible order
    const categoryOrder = CATEGORY_ORDER;
    const groups: Array<{ category: string; folders: typeof filtered; totalPlaces: number }> = [];
    for (const cat of categoryOrder) {
      const catFolders = filtered.filter(f => f.category === cat);
      if (catFolders.length === 0) continue;
      groups.push({ category: cat, folders: catFolders, totalPlaces: catFolders.reduce((sum, f) => sum + f.placeCount, 0) });
    }
    return groups;
  }, [isPlacesContainer, placeFolders, allPlaces, folderSearch]);

  return { places, groupedFolders };
}
